import type { IgfdPaymentPlan, Restaurant } from "api-types";
import type { PersistConfig } from "redux-persist/es/types";

import { createHybridStorage } from "@dashboard/store/storage/hybridStorage";
import type {
  ReduxState,
  RestaurantDraft,
  RestaurantWithPaymentPlan,
} from "@dashboard/types";

export type RestaurantSelectedAction = {
  type: "RESTAURANT_SELECTED";
  // A selected restaurant always carries its payment plan; null if none is selected.
  payload: RestaurantWithPaymentPlan | null;
};
// Dispatched alongside RESTAURANT_SELECTED only when the selection moved to a
// different restaurant. Per-restaurant data slices reset on this action, not on
// RESTAURANT_SELECTED: their refetch triggers are keyed on the restaurant uuid,
// so resetting on a same-restaurant re-select would strand the initial-loading
// flags with no refetch scheduled.
export type RestaurantChangedAction = { type: "RESTAURANT_CHANGED" };
// Actions for modifications on the Restaurant entity
export type RestaurantReducerAction =
  | RestaurantSelectedAction // When the current restaurant is selected / updated from the header.
  | RestaurantChangedAction
  | { type: "FETCHED_RESTAURANTS"; payload: RestaurantWithPaymentPlan[] } // All restaurants are fetched from the API.
  | { type: "RESTAURANT_CREATED"; payload: RestaurantWithPaymentPlan } // New restaurant is created.
  | { type: "RESTAURANT_DETAILS_UPDATED"; payload: Partial<RestaurantDraft> } // Restaurant details are updated (price range, website, etc.)
  | {
      type: "RESTAURANT_PLAN_UPDATED";
      payload: {
        restaurantUuid: string;
        restaurant: Restaurant;
        paymentPlan: IgfdPaymentPlan;
      };
    }; // A restaurant flag was edited (admin); both restaurant and plan are refreshed.

// If the state is persisted to storage, this is the config used for that persist.
// We don't persist during storybook.
// Uses hybrid storage: sessionStorage for tab-specific state, localStorage for persistence.
export const restaurantReducerPersistConfig = {
  key: "igfd-dashboard-restaurant",
  storage: createHybridStorage(),
  whitelist: ["selectedRestaurant", "paymentPlansByRestaurantUuid"],
} as PersistConfig<ReduxState["restaurant"]>;

export const restaurantReducer: (
  state: ReduxState["restaurant"] | undefined,
  action: RestaurantReducerAction
) => ReduxState["restaurant"] = (
  state: ReduxState["restaurant"] = {
    initialRestaurantsLoading: true,
    accessibleRestaurants: [],
    selectedRestaurant: null,
    paymentPlansByRestaurantUuid: {} as {
      [restaurantUuid: string]: IgfdPaymentPlan;
    },
  },
  action: RestaurantReducerAction
) => {
  switch (action.type) {
    case "FETCHED_RESTAURANTS":
      // When all restaurants are fetched from the API.
      const paymentPlansByRestaurantUuid: {
        [restaurantUuid: string]: IgfdPaymentPlan;
      } = action.payload.reduce(
        (acc, restaurantWithPaymentPlan) => ({
          ...acc,
          [restaurantWithPaymentPlan.restaurant.uuid]:
            restaurantWithPaymentPlan.paymentPlan,
        }),
        {}
      );

      return {
        ...state,
        initialRestaurantsLoading: false,
        accessibleRestaurants: action.payload.map(
          (restaurantWithPaymentPlan) => restaurantWithPaymentPlan.restaurant
        ),
        // Merge fresh plans over persisted ones, so a persisted selection that
        // is not in the accessible set (admins) keeps its plan until refetched.
        paymentPlansByRestaurantUuid: {
          ...state.paymentPlansByRestaurantUuid,
          ...paymentPlansByRestaurantUuid,
        },
      } as ReduxState["restaurant"];

    case "RESTAURANT_CREATED":
      // When a new restaurant is created.
      return {
        ...state,
        accessibleRestaurants: [
          ...state.accessibleRestaurants,
          action.payload.restaurant,
        ],
        // set the newly created restaurant as the selected restaurant
        selectedRestaurant: action.payload.restaurant,
        paymentPlansByRestaurantUuid: {
          ...state.paymentPlansByRestaurantUuid,
          [action.payload.restaurant.uuid]: action.payload.paymentPlan,
        },
      } as ReduxState["restaurant"];
    case "RESTAURANT_SELECTED":
      // When the current restaurant is selected / updated from the header or
      // initial load. The plan is set together with the restaurant.
      // Null payload = no restaurant available.
      return {
        ...state,
        selectedRestaurant: action.payload?.restaurant ?? null,
        paymentPlansByRestaurantUuid: action.payload
          ? {
              ...state.paymentPlansByRestaurantUuid,
              [action.payload.restaurant.uuid]: action.payload.paymentPlan,
            }
          : state.paymentPlansByRestaurantUuid,
      } as ReduxState["restaurant"];
    case "RESTAURANT_PLAN_UPDATED":
      return {
        ...state,
        selectedRestaurant:
          state.selectedRestaurant?.uuid === action.payload.restaurantUuid
            ? action.payload.restaurant
            : state.selectedRestaurant,
        paymentPlansByRestaurantUuid: {
          ...state.paymentPlansByRestaurantUuid,
          [action.payload.restaurantUuid]: action.payload.paymentPlan,
        },
      } as ReduxState["restaurant"];
    case "RESTAURANT_DETAILS_UPDATED":
      // a partial set of fields may have been updated.
      // Here we merge the updated fields with the existing restaurant.
      const updatedRestaurant = mergeDraft(
        action.payload,
        state.selectedRestaurant as Restaurant
      );
      return {
        ...state,
        selectedRestaurant: updatedRestaurant,
      } as ReduxState["restaurant"];
    default:
      return state;
  }
};

// Apply the draft to the restaurant.
const mergeDraft: (
  draft: Partial<RestaurantDraft>,
  restaurant: Restaurant
) => Restaurant = (draft: Partial<RestaurantDraft>, restaurant: Restaurant) => {
  return {
    ...restaurant,
    ...draft,
    address: {
      ...restaurant.address,
      ...draft.address,
    },
  } as Restaurant;
};
